import React from 'react';
import { useTranslation } from 'react-i18next';
import type { WhoopHealthSummary } from '../services/api';

interface Props { summary: WhoopHealthSummary; }

const METRICS: Array<{ key: keyof WhoopHealthSummary['metrics']; unitKey: string }> = [
  { key: 'recovery_score', unitKey: 'percent' },
  { key: 'resting_heart_rate', unitKey: 'bpm' },
  { key: 'spo2', unitKey: 'percent' },
  { key: 'skin_temperature', unitKey: 'celsius' },
  { key: 'respiratory_rate', unitKey: 'breathsPerMinute' },
  { key: 'strain', unitKey: 'score' },
];

function formatValue(value: number | string): string {
  if (typeof value !== 'number') return value;
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

export function WhoopHealthCard({ summary }: Props) {
  const { t, i18n } = useTranslation();
  const latestLabel = summary.latest_at
    ? new Intl.DateTimeFormat(i18n.language, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
      .format(new Date(summary.latest_at))
    : t('home.whoop.noData');

  return (
    <section className="whoop-health-card" style={styles.card} aria-label={t('home.whoop.title')}>
      <div style={styles.header}>
        <div style={styles.identity}>
          <span style={styles.logo} aria-hidden="true">W</span>
          <div>
            <h3 style={styles.title}>{t('home.whoop.title')}</h3>
            <p style={styles.subtitle}>{t('home.whoop.subtitle')}</p>
          </div>
        </div>
        <span style={styles.synced}>{t('home.whoop.updated', { value: latestLabel })}</span>
      </div>
      <div style={styles.grid}>
        {METRICS.map(({ key, unitKey }) => {
          const metric = summary.metrics[key];
          return (
            <div className="whoop-health-card__metric" key={key} style={styles.metric}>
              <span style={styles.metricLabel}>{t(`home.whoop.metrics.${key}`)}</span>
              <strong style={styles.metricValue} className="ltr-island">{metric ? formatValue(metric.value) : '—'}</strong>
              <span style={styles.unit}>{metric ? t(`home.whoop.units.${unitKey}`) : t('home.whoop.unavailable')}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: { display: 'flex', flexDirection: 'column', gap: 14, padding: 16, borderRadius: 'var(--r-4)', background: 'var(--surface-0)', boxShadow: 'var(--nm-raised)' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' },
  identity: { display: 'flex', alignItems: 'center', gap: 10 },
  logo: { width: 34, height: 34, borderRadius: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#171717', color: '#fff', fontSize: 16, fontWeight: 900, fontFamily: 'var(--font-display)' },
  title: { margin: 0, color: 'var(--fg-1)', fontSize: 14, fontWeight: 800, fontFamily: 'var(--font-display)' },
  subtitle: { margin: '2px 0 0', color: 'var(--fg-4)', fontSize: 10 },
  synced: { color: 'var(--fg-4)', fontSize: 10 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 9 },
  metric: { minWidth: 0, padding: '11px 10px', borderRadius: 'var(--r-3)', background: 'var(--surface-sunken)', boxShadow: 'var(--nm-inset-sm)', display: 'flex', flexDirection: 'column', gap: 3, textAlign: 'start' },
  metricLabel: { color: 'var(--fg-3)', fontSize: 10, fontWeight: 700 },
  metricValue: { color: 'var(--fg-1)', fontSize: 20, lineHeight: 1.1, fontFamily: 'var(--font-mono)' },
  unit: { color: 'var(--fg-4)', fontSize: 9 },
};
